// Adapted from https://github.com/0x006F/react-media-recorder

// This hook supports media capture from browser, using the
// MediaRecorder API (https://developer.mozilla.org/en-US/docs/Web/API/MediaRecorder)
// This hook is ready to handle both audio and video
// On Safari, this should work in versions earlier than 14.0.2 thanks to the audio-recorder-polyfill
// Chrome/iOS doesn't support this feature

import { useCallback, useEffect, useRef, useState } from 'react'
import R from 'ramda'
import PolyfillAudioRecorder from 'audio-recorder-polyfill'
import mpegEncoder from 'audio-recorder-polyfill/mpeg-encoder'

import { recorderError, recorderStatus } from 'Common/constants/mediaRecorder'

export default function useMediaRecorder({
  audio = true,
  onStop = () => null,
  video = false,
} = {}) {
  const mediaObject = useRef([])
  const mediaRecorder = useRef(null)
  const mediaStream = useRef(null)

  const [status, setStatus] = useState('idle')
  const [error, setError] = useState(null)
  const [shouldUsePolyfill, setShouldUsePolyfill] = useState(false)
  const [isAudioMuted, setIsAudioMuted] = useState(false)
  const [mediaBlobUrl, setMediaBlobUrl] = useState(null)
  const [base64Media, setBase64Media] = useState(null)

  // mediaTypes is used as options passed in to
  // the new mediaRecorder object and the new Blob object,
  // when the media is being captured

  const mediaTypes = {
    video: 'video/mp4',
    audio: 'audio/mpeg',
  }

  const mediaOptions = {
    mimeType: video ? mediaTypes.video : mediaTypes.audio,
  }

  const getMediaStream = useCallback(async () => {
    setStatus(recorderStatus.IDLE)
    const mediaConstraints = {
      audio: typeof audio === 'boolean' ? !!audio : audio,
      video: typeof video === 'boolean' ? !!video : video,
    }

    try {
      const stream = await window.navigator.mediaDevices.getUserMedia(
        mediaConstraints
      )

      mediaStream.current = stream
      setStatus(recorderStatus.IDLE)
    } catch (e) {
      e.name === 'NotAllowedError'
        ? setError(recorderError.NOT_ALLOWED)
        : setError(e.name)
      setStatus(recorderStatus.BLOCKED)
    }
  }, [audio, video])

  // This sets the polyfill to be used in all browsers. At first, it was being used only
  // for Safari. Howver we noticed incompatibility between audio file recorded by different
  // browsers during audio execution. The polyfill comes in handy since it has an audio
  // encoder, which allows us to unify the audio format for all browsers.
  useEffect(() => {
    PolyfillAudioRecorder.encoder = mpegEncoder
    window.MediaRecorder = PolyfillAudioRecorder
    setShouldUsePolyfill(true)
  }, [])

  useEffect(() => {
    const checkConstraints = mediaType => {
      const supportedMediaConstraints = navigator.mediaDevices.getSupportedConstraints()
      const unSupportedConstraints = Object.keys(mediaType).filter(
        constraint => !supportedMediaConstraints[constraint]
      )

      if (unSupportedConstraints.length > 0) {
        throw new Error(
          `Constraints not supported: '${unSupportedConstraints.join(
            ','
          )}'. Please check them out.`
        )
      }
    }

    if (typeof audio === 'object') {
      checkConstraints(audio)
    }
    if (typeof video === 'object') {
      checkConstraints(video)
    }
  }, [audio, video])

  // Media Recorder Handlers

  const onRecordingStop = () => {
    const blob = new Blob(mediaObject.current, mediaOptions)
    const url = URL.createObjectURL(blob)

    // Converting the blob to Base64 so that we can POST
    // via `postAttempt` to the backend and upload it to AWS

    const reader = new FileReader()
    reader.onloadend = () => {
      const encodedBlob = reader.result

      // This workaround forces the data description on base64 strings
      // due sometimes it returns empty or containing the wrong info
      const newEncodedBlob = R.replace(
        /(data:;|data:application\/octet-stream;)/,
        `data:${video ? mediaTypes.video : mediaTypes.audio};`,
        encodedBlob
      )
      setBase64Media(newEncodedBlob)
    }
    reader.readAsDataURL(blob)

    setStatus(recorderStatus.STOPPED)
    setMediaBlobUrl(url)
    onStop({ url, blob }) // base64Media will return as null if not set
  }

  const onRecordingActive = ({ data }) => {
    mediaObject.current.push(data)
  }

  const startRecording = async () => {
    setError(null)
    if (!mediaStream.current) {
      await getMediaStream()
    }
    if (mediaStream.current) {
      const hasStreamEnded = mediaStream.current
        .getTracks()
        .some(track => track.readyState === 'ended')
      if (hasStreamEnded) {
        await getMediaStream()
      }
      mediaRecorder.current = new MediaRecorder(
        mediaStream.current,
        mediaOptions
      )

      // The polyfill we're using has its own way to handle events,
      // and they're not the same as the ones emitted by the
      // mediaRecorder API. For this reason we need to create event
      // listeners in order to listen the polyfill events.
      if (shouldUsePolyfill) {
        mediaRecorder.current.addEventListener('dataavailable', event =>
          onRecordingActive(event)
        )
        mediaRecorder.current.addEventListener('stop', event =>
          onRecordingStop(event)
        )
        mediaRecorder.current.addEventListener('error', () => {
          setError(recorderError.RECORDER_ERROR)
          setStatus(recorderStatus.IDLE)
        })
      } else {
        mediaRecorder.current.ondataavailable = onRecordingActive
        mediaRecorder.current.onstop = onRecordingStop
        mediaRecorder.current.onerror = () => {
          setError(recorderError.RECORDER_ERROR)
          setStatus(recorderStatus.IDLE)
        }
      }

      mediaRecorder.current.start()
      setStatus(recorderStatus.RECORDING)
    }
  }

  const muteAudio = mute => {
    setIsAudioMuted(mute)
    if (!mediaStream.current) return

    mediaStream.current.getAudioTracks().forEach(audioTrack => {
      const track = audioTrack
      track.enabled = !mute
      return track
    })
  }

  const stopRecording = () => {
    if (mediaRecorder.current?.state !== 'inactive') {
      setStatus(recorderStatus.STOPPED)

      mediaRecorder.current.stop()

      mediaStream.current &&
        mediaStream.current.getTracks().forEach(track => track.stop())

      mediaObject.current = []
    }
  }

  return {
    base64Media,
    clearBlobUrl: () => setMediaBlobUrl(null),
    error,
    isAudioMuted,
    mediaBlobUrl,
    muteAudio: () => muteAudio(true),
    previewStream: mediaStream.current
      ? new MediaStream(mediaStream.current.getVideoTracks())
      : null,
    startRecording,
    status,
    stopRecording,
    unMuteAudio: () => muteAudio(false),
    setStatus,
  }
}
